import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import { resolveCompany, resetPassword, errorOf } from '@/lib/tenantAuth';
import AuthShell from './AuthShell';

/**
 * The emailed reset link (/c/:slug/reset-password?token=…, screen A08). The token is
 * single-use and short-lived; the server ends every session and trusted device of the
 * account on success, so the next step is a fresh sign-in — two-step verification, when
 * enrolled, still applies.
 */
export default function ResetPassword() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [company, setCompany] = useState(null);
  const [preauthId, setPreauthId] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    resolveCompany(slug)
      .then((res) => { setCompany(res.company); setPreauthId(res.preauthId); })
      .catch((ex) => setErr(errorOf(ex).message));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setErr('The two passwords do not match'); return; }
    setErr('');
    setBusy(true);
    try {
      await resetPassword(preauthId, token, password);
      setDone(true);
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
      setPassword('');
      setConfirm('');
    }
  };

  return (
    <AuthShell title="Choose a new password" subtitle={company ? company.displayName : null}>
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {done ? (
          <>
            <Alert variant="success">Your password was changed and you were signed out everywhere.</Alert>
            <Link className="block text-center text-sm text-primary-600 hover:underline" to={`/c/${slug}/login`}>Sign in</Link>
          </>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            {!token && <Alert variant="error">This link is incomplete — open it from the email again.</Alert>}
            <Input label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <Input label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            <Button type="submit" className="w-full" disabled={busy || !token || !preauthId || !password}>{busy ? 'Saving…' : 'Change password'}</Button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
